import {
  ResourceCategoryDocument,
  type ResourceCategoryQuery,
  useResourceCategoriesQuery,
} from "@graphql/resources/ResourceCategories.g";
import { useInfiniteQuery } from "@tanstack/react-query";
import { useDebounce } from "@uidotdev/usehooks";
import { useCallback, useMemo } from "react";

import {
  InfiniteSelectBase,
  InfiniteSelectDataHook,
  SelectOption,
} from "@components/Selects/InfiniteSelectBase";

const PAGE_SIZE = 30;
const SEARCH_DEBOUNCE = 300;

// Hook to provide categories data for the infinite select
const useInfiniteCategories = (
  searchQuery: string,
  isOpen: boolean,
  disabled: boolean,
): InfiniteSelectDataHook => {
  const debouncedSearchQuery = useDebounce(searchQuery, SEARCH_DEBOUNCE);

  const query = useInfiniteQuery({
    queryKey: ["InfiniteCategories", debouncedSearchQuery, { parent_Isnull: true }],
    queryFn: async ({ pageParam }) => {
      const result = await useResourceCategoriesQuery.fetcher({
        first: PAGE_SIZE,
        offset: pageParam as number,
        parent_Isnull: true,
        ...(debouncedSearchQuery && {
          name_Icontains: debouncedSearchQuery,
        }),
      })();
      return result;
    },
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
      const currentOffset = allPages.length * PAGE_SIZE;
      const totalCount = lastPage?.resourceCategories?.totalCount || 0;

      if (currentOffset < totalCount) {
        return currentOffset;
      }
      return undefined;
    },
    enabled: isOpen && !disabled,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  const options = useMemo(() => {
    const allOptions: Array<{ id: string; label: string }> = [];

    if (query.data?.pages) {
      query.data.pages.forEach((page) => {
        const categories = page?.resourceCategories?.edges || [];
        categories.forEach((edge) => {
          const node = edge?.node;
          if (node) {
            allOptions.push({
              id: node.id,
              label: node.name || "",
            });
          }
        });
      });
    }

    return allOptions;
  }, [query.data]);

  return {
    ...query,
    options,
  };
};

type Props = {
  value: string | undefined;
  onChange: (value: string | undefined) => void;
  placeholder?: string;
  disabled?: boolean;
};

export const InfiniteCategorySelect = ({
  value,
  onChange,
  placeholder = "Search and select category...",
  disabled = false,
}: Props) => {
  const fetchSelectedOption = useCallback(
    async (id: string): Promise<SelectOption | null> => {
      try {
        const response = await fetch(
          import.meta.env.VITE_API_URL_GRAPHQL as string,
          {
            method: "POST",
            credentials: "include",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              query: ResourceCategoryDocument,
              variables: { id },
            }),
          },
        );

        const json = await response.json();

        if (json.errors) {
          console.error("GraphQL error fetching category:", json.errors);
          return null;
        }

        const data = json.data as ResourceCategoryQuery;
        const category = data?.resourceCategory;

        if (category) {
          return {
            id: category.id,
            label: category.name || "",
          };
        }
      } catch (error) {
        console.error("Failed to fetch selected category:", error);
      }
      return null;
    },
    [],
  );

  // Create a wrapper hook that includes disabled in closure
  const useDataWithDisabled = useMemo(() => {
    return (searchQuery: string, isOpen: boolean) => {
      // eslint-disable-next-line react-hooks/rules-of-hooks
      return useInfiniteCategories(searchQuery, isOpen, disabled);
    };
  }, [disabled]);

  return (
    <InfiniteSelectBase
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      useData={useDataWithDisabled}
      loadingText="Loading categories..."
      noResultsText="No categories found"
      loadingMoreText="Loading more..."
      disabled={disabled}
      fetchSelectedOption={fetchSelectedOption}
    />
  );
};
